import React, { useState, useEffect } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Ticket, Trophy, Trash2, RotateCcw, Sparkles } from 'lucide-react';
import { Label } from '../components/ui/label';
import { formatUKDateTime } from '../utils/ukTime';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RaffleAdminPage() {
  usePageTitle('Raffle Admin');
  
  const [currentRaffle, setCurrentRaffle] = useState({ entries: [], stats: {} });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [winner, setWinner] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [deleteDrawId, setDeleteDrawId] = useState(null);
  const [smsTemplate, setSmsTemplate] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [currentRes, historyRes, templateRes] = await Promise.all([
        axios.get(`${API}/raffle/admin/current`, headers),
        axios.get(`${API}/raffle/admin/history`, headers),
        axios.get(`${API}/raffle/admin/sms-template`, headers)
      ]);
      
      setCurrentRaffle(currentRes.data);
      setHistory(historyRes.data.draws || []);
      setSmsTemplate(templateRes.data.template);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load raffle data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const handleDraw = async () => {
    if (currentRaffle.entries.length === 0) {
      toast.error('No entries to draw from');
      return;
    }
    
    setDrawing(true);
    
    try {
      const response = await axios.post(`${API}/raffle/admin/draw`, {}, headers);
      setWinner(response.data);
      setShowWinner(true);
      
      // Reload data
      await loadData();
      
      toast.success('Winner drawn!');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Draw failed');
    } finally {
      setDrawing(false);
    }
  };
  
  const handleClear = async () => {
    try {
      await axios.post(`${API}/raffle/admin/clear`, {}, headers);
      toast.success('Raffle cleared. Numbers will continue from where they left off.');
      setShowClearDialog(false);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to clear raffle');
    }
  };
  
  const handleDeleteDraw = async (drawId) => {
    try {
      await axios.delete(`${API}/raffle/admin/history/${drawId}`, headers);
      toast.success('Draw deleted from history');
      setDeleteDrawId(null);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete draw');
    }
  };
  
  const handleSaveTemplate = async () => {
    if (!smsTemplate.includes('{raffle_numbers}')) {
      toast.error('Template must include {raffle_numbers} placeholder');
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/raffle/admin/sms-template`, { template: smsTemplate }, headers);
      toast.success('SMS template updated');
      setEditingTemplate(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Raffle Admin</h1>
            <p className="text-gray-500">Manage raffle entries and draws</p>
          </div>
          <Button onClick={() => window.location.href = '/raffle'} variant="outline">
            Public Page
          </Button>
        </div>
        
        {/* Current Raffle */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Current Raffle</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {currentRaffle.stats.totalEntries} entries • {currentRaffle.stats.totalNumbers} numbers • £{currentRaffle.stats.totalRevenue}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDraw}
                  disabled={drawing || currentRaffle.entries.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {drawing ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Drawing...
                    </>
                  ) : (
                    <>
                      <Trophy className="w-4 h-4 mr-2" />
                      Draw Winner
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => setShowClearDialog(true)}
                  disabled={currentRaffle.entries.length === 0}
                  variant="destructive"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear Raffle
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {currentRaffle.entries.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Ticket className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No entries yet</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numbers</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SMS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentRaffle.entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{entry.firstName} {entry.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{entry.phone}</td>
                        <td className="px-4 py-3 text-sm text-purple-600 font-mono">
                          {entry.numbers.slice(0, 3).join(', ')}
                          {entry.numbers.length > 3 && ` +${entry.numbers.length - 3} more`}
                        </td>
                        <td className="px-4 py-3 text-sm">{entry.quantity}</td>
                        <td className="px-4 py-3 text-sm font-semibold">£{entry.price}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            entry.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {entry.paymentMethod === 'cash' ? 'Cash' : 'Card'} - {entry.paymentStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {entry.smsSent ? '✓' : '✗'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* SMS Template Settings */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">SMS Template</CardTitle>
                <p className="text-sm text-gray-500">Customize the SMS confirmation message</p>
              </div>
              {!editingTemplate && (
                <Button onClick={() => setEditingTemplate(true)} variant="outline">
                  Edit Template
                </Button>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {editingTemplate ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="smsTemplate">SMS Message Template</Label>
                  <textarea
                    id="smsTemplate"
                    value={smsTemplate}
                    onChange={(e) => setSmsTemplate(e.target.value)}
                    className="w-full min-h-[150px] p-3 border rounded-md font-mono text-sm"
                    placeholder="Enter your SMS template..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Use <code className="bg-gray-100 px-1 py-0.5 rounded">{'{raffle_numbers}'}</code> placeholder for ticket numbers
                  </p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Preview Example:</p>
                  <div className="bg-white p-3 rounded border text-sm font-mono whitespace-pre-wrap">
                    {smsTemplate.replace('{raffle_numbers}', '0048-0052')}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={handleSaveTemplate} disabled={savingTemplate} className="bg-green-600 hover:bg-green-700">
                    {savingTemplate ? 'Saving...' : 'Save Template'}
                  </Button>
                  <Button onClick={() => {
                    setEditingTemplate(false);
                    loadData();
                  }} variant="outline">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="bg-gray-50 p-4 rounded-md font-mono text-sm whitespace-pre-wrap mb-4">
                  {smsTemplate}
                </div>
                <p className="text-xs text-gray-500">
                  This message will be sent to buyers after purchase. Numbers will be formatted as 0001-0015 for ranges or 0001, 0023, 0045 for individual tickets.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* History */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-xl">Draw History</CardTitle>
            <p className="text-sm text-gray-500">All past winners</p>
          </CardHeader>
          
          <CardContent className="p-0">
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No draws yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {history.map((draw) => (
                  <div key={draw.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="font-bold text-lg">#{draw.winningNumber}</span>
                        <span className="text-gray-400">•</span>
                        <span className="font-semibold">{draw.winnerName}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Phone: {draw.winnerPhone} • Numbers: {draw.winnerAllNumbers.join(', ')}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Drawn {formatUKDateTime(draw.drawnAt)} • {draw.totalEntries} entries • {draw.totalNumbers} numbers
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteDrawId(draw.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Winner Dialog */}
      <Dialog open={showWinner} onOpenChange={setShowWinner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">🎉 Winner! 🎉</DialogTitle>
          </DialogHeader>
          
          {winner && (
            <div className="space-y-4 text-center">
              <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-400">
                <div className="text-sm text-gray-600 mb-1">Winning Number</div>
                <div className="text-5xl font-bold text-yellow-600">#{winner.winning_number}</div>
              </div>
              
              <div className="text-left bg-gray-50 p-4 rounded-lg">
                <div className="mb-2">
                  <div className="text-sm text-gray-600">Winner</div>
                  <div className="text-xl font-bold">{winner.winner_name}</div>
                </div>
                <div className="mb-2">
                  <div className="text-sm text-gray-600">Phone</div>
                  <div className="font-semibold">{winner.winner_phone}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">All Numbers</div>
                  <div className="font-mono text-sm text-purple-600">
                    {winner.winner_all_numbers.join(', ')}
                  </div>
                </div>
              </div>
              
              <Button onClick={() => setShowWinner(false)} className="w-full">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Clear Confirmation */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Current Raffle?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all {currentRaffle.entries.length} entries from the current raffle. Numbers will continue from where they left off.
              <br /><br />
              Past draw history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} className="bg-red-600 hover:bg-red-700">
              Clear Raffle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Draw Confirmation */}
      <AlertDialog open={!!deleteDrawId} onOpenChange={() => setDeleteDrawId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draw from History?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this draw from history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteDraw(deleteDrawId)} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
